import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Button, useSlotRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import {HeaderSearchIcon} from '../../../icons'
import {useSearchOverlay} from '../../search-overlay'
import HeaderIconButton from './header-icon-button'
import {searchCtaRecipe} from './search-cta.recipe'

/**
 * Header search CTA.
 *
 * Renders the desktop icon action or the mobile input-style search trigger;
 * both presentations open the shared search overlay and own their labels here.
 *
 * @component
 * @param {boolean} [props.isNavigationOpen] - Whether the mobile navigation is open.
 * @param {Function} [props.onClick] - Closes header UI that cannot coexist with search.
 * @param {'desktop'|'mobile'} [props.variant] - Search CTA presentation.
 * @param {React.RefObject} [props.portalContainer] - Header card that anchors the overlay.
 * @returns {JSX.Element}
 */
const SearchCta = ({isNavigationOpen, onClick, portalContainer, variant}) => {
    const {formatMessage} = useIntl()
    const {open} = useSearchOverlay()
    const recipe = useSlotRecipe({recipe: searchCtaRecipe})
    const styles = useMemo(
        () => recipe({variant, open: variant === 'mobile' && isNavigationOpen}),
        [isNavigationOpen, recipe, variant]
    )
    const searchLabel = formatMessage({
        id: 'global.search',
        defaultMessage: 'Search'
    })

    const handleClick = () => {
        onClick?.()
        open({portalContainer})
    }

    if (variant === 'mobile') {
        return (
            <Button
                unstyled
                type="button"
                css={styles.root}
                aria-label={searchLabel}
                onClick={handleClick}
            >
                <HeaderSearchIcon css={styles.icon} />
                <span>{searchLabel}</span>
            </Button>
        )
    }

    return (
        <HeaderIconButton
            css={styles.root}
            onClick={handleClick}
            aria-label={formatMessage({
                id: 'header.button.assistive_msg.search',
                defaultMessage: 'Show search toolbar'
            })}
        >
            <HeaderSearchIcon css={styles.icon} />
        </HeaderIconButton>
    )
}

SearchCta.propTypes = {
    isNavigationOpen: PropTypes.bool,
    onClick: PropTypes.func,
    portalContainer: PropTypes.object,
    variant: PropTypes.oneOf(Object.keys(searchCtaRecipe.variants.variant))
}

SearchCta.defaultProps = {
    isNavigationOpen: false,
    variant: 'desktop'
}

if (process.env.NODE_ENV !== 'production') {
    SearchCta.displayName = 'SearchCta'
}

export default SearchCta
